import { User as DBUser } from "../services/api";

export * from './textFormatting';
export * from './nutritionCalculations';
export * from './mealPlanFormatting';
export * from './mealPlanSimplification';
export * from './likesDislikes';
export * from './localStorage';
export * from './modelOptions';

/**
 * Check if a user has access to use the service
 * Access is granted if:
 * 1. User has a Passio API key (legacy)
 * 2. User has an active premium subscription
 * 3. User has free runs remaining
 */
export const hasServiceAccess = (dbUser: DBUser | null): boolean => {
    if (!dbUser) return false;
    
    // Check for API key (legacy support)
    if (dbUser.passioapikey) return true;
    
    // Check for premium subscription
    if (dbUser.subscription_status === 'premium') return true;
    
    // Check for trial subscription
    if (dbUser.subscription_status === 'trial') return true;
    
    // Check for free runs
    if (dbUser.free_runs_remaining > 0) return true;
    
    return false;
};

/**
 * Get user's access status details
 */
export const getUserAccessStatus = (dbUser: DBUser | null) => {
    if (!dbUser) {
        return {
            hasAccess: false,
            hasApiKey: false,
            isPremium: false,
            isTrial: false,
            hasFreeRuns: false,
            freeRunsRemaining: 0,
            hasValidPaymentMethod: false,
            hasUsedFreeTrial: false,
            canGetFreeTrial: false,
        };
    }
    
    const hasApiKey = !!dbUser.passioapikey;
    const isPremium = dbUser.subscription_status === 'premium';
    const isTrial = dbUser.subscription_status === 'trial';
    const hasFreeRuns = dbUser.free_runs_remaining > 0;
    const canGetFreeTrial = dbUser.has_valid_payment_method === false && dbUser.has_used_free_trial === false;
    
    return {
        hasAccess: hasApiKey || isPremium || isTrial || hasFreeRuns,
        hasApiKey,
        isPremium,
        isTrial,
        hasFreeRuns,
        freeRunsRemaining: dbUser.free_runs_remaining,
        hasValidPaymentMethod: dbUser.has_valid_payment_method,
        hasUsedFreeTrial: dbUser.has_used_free_trial,
        canGetFreeTrial,
    };
};

/**
 * Check if a query parameter exists in the current browser URL
 * @param paramName - The name of the query parameter to check
 * @returns The value of the query parameter if it exists, null otherwise
 */
export function getUrlQueryParam(paramName: string): string | null {
    if (typeof window === 'undefined') return null;
    
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
}

/**
 * Check if skipRefine query parameter is set to true in the browser URL
 * @returns true if ?skipRefine=true is in the URL, false otherwise
 */
export function shouldSkipRefine(): boolean {
    const skipRefineParam = getUrlQueryParam('skipRefine');
    return skipRefineParam === 'true';
}

// Helper functions to safely access API config values
export function getUserApiModel(user: DBUser | null): string | undefined {
  return user?.apiconfig?.model;
}

export function getUserApiRefinementModel(user: DBUser | null): string | undefined {
  return user?.apiconfig?.refinementmodel;
}

export function getUserApiDatasourceConfig(user: DBUser | null): Record<string, any> {
  return user?.apiconfig?.datasourceconfig || {};
}

export function getUserApiConfig(user: DBUser | null): {
  model?: string;
  refinementModel?: string;
  datasourceConfig: Record<string, any>;
} {
  return {
    model: getUserApiModel(user),
    refinementModel: getUserApiRefinementModel(user),
    datasourceConfig: getUserApiDatasourceConfig(user),
  };
}

/**
 * Adds day count reinforcement to the beginning and end of a prompt
 * to ensure the AI generates the correct number of days in the meal plan
 * @param prompt The original prompt
 * @param numberOfDays The number of days to generate
 * @returns The prompt with day count reinforcement added
 */
export const addDayCountReinforcement = (prompt: string, numberOfDays: number): string => {
    const reinforcement = `EXPLICITLY generate ${numberOfDays} objects in mealPlanDays, creating ${numberOfDays} days worth of meal plans.`;
    return `${reinforcement}\n\n${prompt}\n\n${reinforcement}`;
};
